import { env } from '@/configs/env';
import {
  ACCESS_TOKEN_MAX_AGE_SECONDS,
  deleteCookie,
  getCookie,
  setCookie,
} from './cookies';

export const BASE_URL = env.apiBaseUrl;
export const API_KEY = env.apiKey;

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

const buildHeaders = (
  extra: Record<string, string> | undefined,
  accessToken?: string,
): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
    ...(extra ?? {}),
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  return headers;
};

// Shared with the openapi-fetch client (src/infrastructure/api) so both HTTP
// clients send an expired session to the same place.
export const redirectToLogin = () => {
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
};

interface RefreshedTokens {
  accessToken: string;
  refreshToken: string;
}

// Shared by fetchApi's refresh-on-401 flow and AuthProvider's mount-time
// session check, so the access token cookie's short lifetime does not force
// a re-login whenever the refresh token is still valid.
export const refreshAccessToken = async (): Promise<RefreshedTokens | null> => {
  const refreshToken = getCookie('detecta_web_refreshToken');
  if (!refreshToken) {
    return null;
  }

  const refreshResponse = await fetch(`${BASE_URL}/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!refreshResponse.ok) {
    return null;
  }

  const data = (await refreshResponse.json()) as RefreshedTokens;

  setCookie('detecta_web_accessToken', data.accessToken, {
    maxAge: ACCESS_TOKEN_MAX_AGE_SECONDS,
  });
  setCookie('detecta_web_refreshToken', data.refreshToken, {
    maxAge: 60 * 60 * 24 * 7,
  });

  return data;
};

export const fetchApi = async (
  endpoint: string,
  options: FetchOptions = {},
): Promise<Response> => {
  const url = `${BASE_URL}${endpoint}`;
  const accessToken = getCookie('detecta_web_accessToken');

  const finalHeaders = buildHeaders(options.headers, accessToken);
  if (options.body instanceof FormData) {
    delete finalHeaders['Content-Type'];
  }

  let response = await fetch(url, {
    ...options,
    headers: finalHeaders,
  });

  // Anonymous requests (no refresh token, e.g. login itself or a public
  // invitation lookup) never had a session to expire — a 401 there is a
  // real auth failure the caller should surface, not a stale session to
  // refresh-and-redirect away from.
  if (response.status !== 401 || !getCookie('detecta_web_refreshToken')) {
    return response;
  }

  try {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      deleteCookie('detecta_web_accessToken');
      deleteCookie('detecta_web_refreshToken');
      redirectToLogin();
      return response;
    }

    const retryHeaders = buildHeaders(options.headers, refreshed.accessToken);
    if (options.body instanceof FormData) {
      delete retryHeaders['Content-Type'];
    }

    response = await fetch(url, {
      ...options,
      headers: retryHeaders,
    });
  } catch (error) {
    console.error('Token refresh failed', error);
    deleteCookie('detecta_web_accessToken');
    deleteCookie('detecta_web_refreshToken');
    redirectToLogin();
  }

  return response;
};
